/**
 * EditorPreview — Preview playback controller.
 * Registers a temporary sequence and plays it via CinematicDirector.
 */
import Phaser from 'phaser';
import { EditorEvents } from './EditorTypes';
import type { EditorState } from './EditorTypes';
import { EventBus } from '@/events/EventBus';
import { CinematicEvents } from '../CinematicEvents';
import { registerSequence, CinematicDirector } from '../CinematicDirector';
import { GOD } from '@/config/GodMode';

const PREVIEW_ID = '__preview__';

export class EditorPreview {
  private scene: Phaser.Scene;
  private state: EditorState;
  private prevForceCinematic = false;
  private active = false;
  /** When true, plays only one step then stops and advances selection */
  private singleStepMode = false;

  constructor(scene: Phaser.Scene, state: EditorState) {
    this.scene = scene;
    this.state = state;

    EventBus.on(EditorEvents.PREVIEW_START, this.start, this);
    EventBus.on(EditorEvents.PREVIEW_STOP, this.stop, this);
    EventBus.on(EditorEvents.PREVIEW_SINGLE_STEP, this.startSingleStep, this);
  }

  start(): void {
    if (this.active) return;
    if (this.state.sequence.steps.length === 0) return;

    this.active = true;
    this.state.mode = 'preview';
    this.state.playingStepIndex = 0;

    // Deep-clone sequence with preview ID
    const previewSeq = JSON.parse(JSON.stringify(this.state.sequence));
    previewSeq.id = PREVIEW_ID;
    registerSequence(previewSeq, false);

    // Force cinematic to be playable
    this.prevForceCinematic = GOD.forceCinematic;
    GOD.forceCinematic = true;

    // Keep toolbar + timeline visible, hide step panel + live preview
    (this.scene as any).showPreviewPanels?.();

    // Unlock game input for preview playback (cinematic needs UIScene for skip btn)
    (this.scene as any).lockGameInput?.(false);

    // Resume caller scenes
    for (const key of this.state.callerScenes) {
      const s = this.scene.scene.get(key);
      if (s) this.scene.scene.resume(key);
    }

    // Track playing step in real-time
    EventBus.on(CinematicEvents.STEP_STARTED, this.onStepStarted, this);

    // Listen for completion
    EventBus.once(CinematicEvents.COMPLETE, this.onPreviewDone, this);
    EventBus.once(CinematicEvents.SKIP, this.onPreviewDone, this);

    // Start the cinematic
    EventBus.emit(CinematicEvents.START, { sequenceId: PREVIEW_ID });
  }

  /**
   * Play only the currently selected step, then stop and advance selection.
   * Creates a 1-step preview sequence to reuse all existing infrastructure.
   */
  startSingleStep(): void {
    if (this.active) return;
    const idx = this.state.selectedStepIndex;
    if (idx < 0 || idx >= this.state.sequence.steps.length) return;

    this.singleStepMode = true;
    this.active = true;
    this.state.mode = 'preview';
    this.state.playingStepIndex = idx;

    // Build a 1-step sequence from the selected step
    const step = JSON.parse(JSON.stringify(this.state.sequence.steps[idx]));
    const previewSeq = {
      id: PREVIEW_ID,
      lockCamera: this.state.sequence.lockCamera,
      lockInput: this.state.sequence.lockInput,
      steps: [step],
    };
    registerSequence(previewSeq, false);

    this.prevForceCinematic = GOD.forceCinematic;
    GOD.forceCinematic = true;

    (this.scene as any).showPreviewPanels?.();
    (this.scene as any).lockGameInput?.(false);

    for (const key of this.state.callerScenes) {
      const s = this.scene.scene.get(key);
      if (s) this.scene.scene.resume(key);
    }

    EventBus.on(CinematicEvents.STEP_STARTED, this.onStepStarted, this);
    EventBus.once(CinematicEvents.COMPLETE, this.onSingleStepDone, this);
    EventBus.once(CinematicEvents.SKIP, this.onSingleStepDone, this);

    EventBus.emit(CinematicEvents.START, { sequenceId: PREVIEW_ID });
  }

  stop(): void {
    if (!this.active) return;
    // Force-stop the cinematic immediately (synchronous cleanup).
    // Using FORCE_STOP instead of SKIP avoids the tween-based dismiss
    // that would freeze when we pause the overlay scene moments later,
    // leaving cinematicSpeedMult stuck at 100x on subsequent plays.
    EventBus.emit(CinematicEvents.FORCE_STOP);
    // FORCE_STOP doesn't emit COMPLETE, so clean up directly
    if (this.singleStepMode) {
      this.onSingleStepDone();
    } else {
      this.onPreviewDone();
    }
  }

  private onStepStarted = (data: { stepIndex: number; stepType: string }): void => {
    this.state.playingStepIndex = data.stepIndex;
    EventBus.emit(EditorEvents.STEP_SELECTED); // triggers timeline refresh
  };

  /** Single-step done: stop preview, advance selection to next step */
  private onSingleStepDone = (): void => {
    if (!this.active) return;
    this.active = false;
    this.state.mode = 'edit';
    this.state.playingStepIndex = -1;

    EventBus.off(CinematicEvents.STEP_STARTED, this.onStepStarted, this);
    EventBus.off(CinematicEvents.COMPLETE, this.onSingleStepDone, this);
    EventBus.off(CinematicEvents.SKIP, this.onSingleStepDone, this);
    CinematicDirector.resetCinematic(PREVIEW_ID);
    GOD.forceCinematic = this.prevForceCinematic;

    // Kill any lingering camera effects before pausing
    const hostScene = this.state.hostScene;
    if (hostScene) {
      const cam = hostScene.cameras.main;
      (cam as any).panEffect?.reset();
      (cam as any).zoomEffect?.reset();
    }

    // Re-pause caller scenes (only if running — avoid Phaser warning)
    for (const key of this.state.callerScenes) {
      const s = this.scene.scene.get(key);
      if (s && this.scene.scene.isActive(key)) this.scene.scene.pause(key);
    }
    (this.scene as any).lockGameInput?.(true);

    // Advance selection to next step (wrap around)
    const nextIdx = this.state.selectedStepIndex + 1;
    if (nextIdx < this.state.sequence.steps.length) {
      this.state.selectedStepIndex = nextIdx;
    }
    this.singleStepMode = false;

    (this.scene as any).showPanels?.();
    EventBus.emit(EditorEvents.PREVIEW_STOP);
  };

  private onPreviewDone = (): void => {
    if (!this.active) return;
    this.active = false;
    this.state.mode = 'edit';
    this.state.playingStepIndex = -1;

    // Cleanup
    EventBus.off(CinematicEvents.STEP_STARTED, this.onStepStarted, this);
    EventBus.off(CinematicEvents.COMPLETE, this.onPreviewDone, this);
    EventBus.off(CinematicEvents.SKIP, this.onPreviewDone, this);
    CinematicDirector.resetCinematic(PREVIEW_ID);
    GOD.forceCinematic = this.prevForceCinematic;

    // Kill any lingering camera effects before pausing (they survive pause
    // and would resume fighting with the editor's direct camera manipulation)
    const hostScene = this.state.hostScene;
    if (hostScene) {
      const cam = hostScene.cameras.main;
      (cam as any).panEffect?.reset();
      (cam as any).zoomEffect?.reset();
    }

    // Re-pause caller scenes and re-lock input (only if running — avoid Phaser warning)
    for (const key of this.state.callerScenes) {
      const s = this.scene.scene.get(key);
      if (s && this.scene.scene.isActive(key)) this.scene.scene.pause(key);
    }
    (this.scene as any).lockGameInput?.(true);

    // Restore editor panels
    (this.scene as any).showPanels?.();

    // Notify toolbar to switch back to PLAY button
    EventBus.emit(EditorEvents.PREVIEW_STOP);
  };

  destroy(): void {
    EventBus.off(EditorEvents.PREVIEW_START, this.start, this);
    EventBus.off(EditorEvents.PREVIEW_STOP, this.stop, this);
    EventBus.off(EditorEvents.PREVIEW_SINGLE_STEP, this.startSingleStep, this);
    if (this.active) {
      EventBus.off(CinematicEvents.STEP_STARTED, this.onStepStarted, this);
      EventBus.off(CinematicEvents.COMPLETE, this.onPreviewDone, this);
      EventBus.off(CinematicEvents.SKIP, this.onPreviewDone, this);
      EventBus.off(CinematicEvents.COMPLETE, this.onSingleStepDone, this);
      EventBus.off(CinematicEvents.SKIP, this.onSingleStepDone, this);
    }
  }
}
